import {
  findConflictingPolicyIdsV2,
  intersectDay,
  toWeekSegments,
} from "@app/deploy";
import {
  defaultServiceSizingPolicy,
  defaultServiceSizingPolicyScheduleV2,
} from "@app/schema";
import type {
  AutoscalingTypes,
  DeployServiceSizingPolicy,
  DeployServiceSizingPolicyScheduleV2,
  ServiceSizingPolicyScheduleTypeV2,
} from "@app/types";
import { useState } from "react";
import {
  Banner,
  Button,
  Disclosure,
  IconAlertTriangle,
  IconRefresh,
  IconTrash,
  Input,
  Modal,
  ModalBody,
  ModalHeading,
  ModalOverlay,
  Select,
  Tooltip,
} from "../shared";

// Hover/selection key for baseline (won't collide with a real policy id).
const BASELINE_KEY = "__baseline__";

// ISO weekday numbering: 1 = Monday ... 7 = Sunday (matches schedule's startDay/endDay).
const WEEKDAYS = [
  { iso: 1, label: "Mon" },
  { iso: 2, label: "Tue" },
  { iso: 3, label: "Wed" },
  { iso: 4, label: "Thu" },
  { iso: 5, label: "Fri" },
  { iso: 6, label: "Sat" },
  { iso: 7, label: "Sun" },
] as const;

const DAY_LABEL_BY_ISO = Object.fromEntries(
  WEEKDAYS.map((d) => [d.iso, d.label]),
) as Record<number, string>;

// Date#getUTCDay is 0 = Sunday; convert to the schedule's ISO numbering.
const isoWeekdayUtc = (date: Date) => {
  const jsDay = date.getUTCDay();
  return jsDay === 0 ? 7 : jsDay;
};

const minutesIntoDayUtc = (date: Date) =>
  date.getUTCHours() * 60 + date.getUTCMinutes();

// Days a schedule spans, as short labels; wraps past day 7 to day 1.
const daysTouchedBySchedule = (
  schedule: DeployServiceSizingPolicyScheduleV2,
) => {
  const dayOffset =
    (schedule.endDay - schedule.startDay + 7) % 7 || // same day => 0 unless...
    (schedule.endTime <= schedule.startTime ? 7 : 0); // ...it wraps a full week
  const days: string[] = [];
  for (let i = 0; i <= dayOffset; i++) {
    const iso = ((schedule.startDay - 1 + i) % 7) + 1;
    days.push(DAY_LABEL_BY_ISO[iso]);
  }
  return { days, dayOffset };
};

// "Sat, Sun · 07:00-06:45 +1d" — the summary shown in the hover tooltip.
const summarizeSchedule = (schedule: DeployServiceSizingPolicyScheduleV2) => {
  const { days, dayOffset } = daysTouchedBySchedule(schedule);
  const suffix = dayOffset > 0 ? ` +${dayOffset}d` : "";
  return `${days.join(", ")} · ${schedule.startTime}-${schedule.endTime}${suffix}`;
};

// Days (deduped, Mon..Sun order) + time range across a policy's schedules.
// Schedules are assumed to share one time range; shows the first one's.
const summarizePolicySchedules = (
  policySchedules: DeployServiceSizingPolicyScheduleV2[],
) => {
  if (policySchedules.length === 0) return "No schedule";
  if (policySchedules.length === 1) {
    return summarizeSchedule(policySchedules[0]);
  }
  const touchedDays = new Set<string>();
  for (const schedule of policySchedules) {
    for (const day of daysTouchedBySchedule(schedule).days) {
      touchedDays.add(day);
    }
  }
  const orderedDays = WEEKDAYS.map((d) => d.label).filter((label) =>
    touchedDays.has(label),
  );
  const [first] = policySchedules;
  return `${orderedDays.join(", ")} · ${first.startTime}-${first.endTime}`;
};

interface PolicySegment {
  startPct: number;
  endPct: number;
  colorIndex: number;
  policy: DeployServiceSizingPolicy;
  schedule: DeployServiceSizingPolicyScheduleV2;
}

// Either a policy's color (with its policy/schedule for the tooltip), or
// baseline (null) for uncovered time.
interface DaySegment {
  startPct: number;
  endPct: number;
  colorIndex: number | null;
  policy?: DeployServiceSizingPolicy;
  schedule?: DeployServiceSizingPolicyScheduleV2;
  // Set when more than one policy claims this slice. Overlapping windows are
  // invalid to save, so the slice is drawn as an error rather than a color.
  conflictPolicies?: DeployServiceSizingPolicy[];
}

const MINUTES_PER_DAY = 24 * 60;

// Projects schedules onto the weekdays they cover, keyed by ISO weekday.
// Policies cycle through `colorCount` by position; unknown policies skipped.
export const buildSegmentsByDay = (
  policies: DeployServiceSizingPolicy[],
  schedules: DeployServiceSizingPolicyScheduleV2[],
  colorCount: number,
): Map<number, PolicySegment[]> => {
  const colorIndexByPolicyId = new Map(
    policies.map((policy, i) => [policy.id, i % colorCount]),
  );
  const policyById = new Map(policies.map((policy) => [policy.id, policy]));

  const segmentsByDay = new Map<number, PolicySegment[]>();
  for (const schedule of schedules) {
    const colorIndex = colorIndexByPolicyId.get(schedule.serviceSizingPolicyId);
    const policy = policyById.get(schedule.serviceSizingPolicyId);
    if (colorIndex === undefined || !policy) continue;

    for (const weekSegment of toWeekSegments(schedule)) {
      for (const day of WEEKDAYS) {
        const hit = intersectDay(weekSegment, (day.iso - 1) * MINUTES_PER_DAY);
        if (!hit) continue;
        const existing = segmentsByDay.get(day.iso) ?? [];
        existing.push({
          startPct: (hit[0] / MINUTES_PER_DAY) * 100,
          endPct: (hit[1] / MINUTES_PER_DAY) * 100,
          colorIndex,
          policy,
          schedule,
        });
        segmentsByDay.set(day.iso, existing);
      }
    }
  }
  return segmentsByDay;
};

// Splits segments at every boundary so none overlap, flagging double-claimed
// slices. Half-open, so back-to-back windows don't collide.
export const resolveOverlaps = (
  policySegments: PolicySegment[],
): DaySegment[] => {
  if (policySegments.length < 2) return policySegments;

  const bounds = [
    ...new Set(policySegments.flatMap((s) => [s.startPct, s.endPct])),
  ].sort((a, b) => a - b);

  const result: DaySegment[] = [];
  for (let i = 0; i < bounds.length - 1; i++) {
    const startPct = bounds[i];
    const endPct = bounds[i + 1];
    const mid = (startPct + endPct) / 2;
    const covering = policySegments.filter(
      (s) => s.startPct <= mid && mid < s.endPct,
    );
    if (covering.length === 0) continue; // gap; fillDayGaps paints baseline
    if (covering.length === 1) {
      result.push({ ...covering[0], startPct, endPct });
      continue;
    }
    result.push({
      startPct,
      endPct,
      colorIndex: null,
      conflictPolicies: covering.map((c) => c.policy),
    });
  }
  return result;
};

// Rounding slop guard so back-to-back schedules don't show a baseline sliver.
const PCT_EPSILON = 0.01;

// Fills gaps between policy segments with baseline, covering the full day.
export const fillDayGaps = (policySegments: DaySegment[]): DaySegment[] => {
  const sorted = [...policySegments].sort((a, b) => a.startPct - b.startPct);
  const result: DaySegment[] = [];
  let cursor = 0;

  for (const seg of sorted) {
    if (seg.startPct > cursor + PCT_EPSILON) {
      result.push({ startPct: cursor, endPct: seg.startPct, colorIndex: null });
    }
    result.push(seg);
    cursor = Math.max(cursor, seg.endPct);
  }
  if (cursor < 100 - PCT_EPSILON) {
    result.push({ startPct: cursor, endPct: 100, colorIndex: null });
  }
  return result;
};

// ---------------------------------------------------------------------------
// Local preview state. Nothing in this file touches the store: no selectors,
// no actions, no queries. Swap the seed + handlers for the real API later.
// ---------------------------------------------------------------------------

type ScalingMode = AutoscalingTypes | "off";

const MODE_OPTIONS: { label: string; value: ScalingMode }[] = [
  { label: "Horizontal", value: "horizontal" },
  { label: "Vertical", value: "vertical" },
  { label: "OFF", value: "off" },
];

type SchedulePreviewState = {
  // "off" is not an API value — it maps to scalingEnabled: false.
  mode: ScalingMode;
  baselinePolicy: DeployServiceSizingPolicy;
  policies: DeployServiceSizingPolicy[];
  schedules: DeployServiceSizingPolicyScheduleV2[];
  // Shared with the sidebar/chart so matching rows and bars light up.
  hoveredKey: string | null;
  // Which policy the form on the right is editing.
  selectedKey: string | null;
  // The form's unsaved window, drawn on the chart as it's edited. A draft
  // whose policy is no longer selected is ignored, so nothing clears it.
  draft: {
    policyId: string;
    schedules: DeployServiceSizingPolicyScheduleV2[];
  } | null;
  // Bumped to remount the form, which re-seeds it from the saved policy.
  formKey: number;
  // Reported by the form: true once its inputs differ from the saved policy.
  dirty: boolean;
};

const initialPreviewState = (): SchedulePreviewState => {
  const peakHours = defaultServiceSizingPolicy({
    id: "preview-peak",
    label: "Peak Hours",
    autoscaling: "horizontal",
    scalingEnabled: true,
    minContainers: 4,
    maxContainers: 12,
  });
  const weekendOvernight = defaultServiceSizingPolicy({
    id: "preview-weekend",
    label: "Weekend Overnight",
    autoscaling: "horizontal",
    scalingEnabled: true,
    minContainers: 1,
    maxContainers: 3,
  });

  return {
    mode: "horizontal",
    baselinePolicy: defaultServiceSizingPolicy({
      id: "preview-baseline",
      label: "Baseline",
      autoscaling: "horizontal",
      scalingEnabled: true,
    }),
    policies: [peakHours, weekendOvernight],
    schedules: [
      // Mon-Fri, 09:00-18:00 UTC
      ...[1, 2, 3, 4, 5].map((startDay) =>
        defaultServiceSizingPolicyScheduleV2({
          id: `preview-peak-${startDay}`,
          serviceSizingPolicyId: peakHours.id,
          startDay,
          startTime: "09:00",
          endDay: startDay,
          endTime: "18:00",
        }),
      ),
      // Fri 22:00 through Sat 02:00, and the same window on Sat.
      ...[5, 6].map((startDay) =>
        defaultServiceSizingPolicyScheduleV2({
          id: `preview-weekend-${startDay}`,
          serviceSizingPolicyId: weekendOvernight.id,
          startDay,
          startTime: "22:00",
          endDay: (startDay % 7) + 1,
          endTime: "02:00",
        }),
      ),
    ],
    hoveredKey: null,
    selectedKey: "preview-peak",
    draft: null,
    formKey: 0,
    dirty: false,
  };
};

type PolicyFormValues = {
  label: string;
  scheduleType: ServiceSizingPolicyScheduleTypeV2;
  activeDays: number[]; // ISO weekday, 1 = Monday — daily only
  startDay: number; // range only
  endDay: number; // range only
  startTime: string; // "HH:MM" UTC
  endTime: string; // "HH:MM" UTC
  minContainers: string;
  maxContainers: string;
  scaleUpStep: string;
  scaleDownStep: string;
  maxCpuThreshold: string;
  minCpuThreshold: string;
  // vertical only
  minimumMemory: string;
  maximumMemory: string;
  memScaleUpThreshold: string;
  memScaleDownThreshold: string;
  memCpuRatioRThreshold: string;
  memCpuRatioCThreshold: string;
  percentile: string;
  useHorizontalScale: boolean;
  metricLookbackSeconds: string;
  postScaleUpCooldownSeconds: string;
  postScaleDownCooldownSeconds: string;
  postReleaseCooldownSeconds: string;
};

const ADVANCED_DEFAULTS = defaultServiceSizingPolicy();

// A window wraps past midnight when it doesn't end later than it starts.
const wrapsOvernight = (startTime: string, endTime: string) =>
  endTime <= startTime;

const toFormValues = (
  policy: DeployServiceSizingPolicy,
  policySchedules: DeployServiceSizingPolicyScheduleV2[],
): PolicyFormValues => {
  const [first] = policySchedules;
  return {
    label: policy.label,
    scheduleType: first?.type ?? "daily",
    // A range contributes its start day, so flipping to Daily lands somewhere
    // sensible rather than on an empty week.
    activeDays: policySchedules.map((s) => s.startDay).sort((a, b) => a - b),
    startDay: first?.startDay ?? 5,
    endDay: first?.endDay ?? 7,
    startTime: first?.startTime ?? "09:00",
    endTime: first?.endTime ?? "18:00",
    minContainers: `${policy.minContainers ?? ""}`,
    maxContainers: `${policy.maxContainers ?? ""}`,
    scaleUpStep: `${policy.scaleUpStep}`,
    scaleDownStep: `${policy.scaleDownStep}`,
    maxCpuThreshold: `${policy.maxCpuThreshold ?? ""}`,
    minCpuThreshold: `${policy.minCpuThreshold ?? ""}`,
    minimumMemory: `${policy.minimumMemory ?? ""}`,
    maximumMemory: `${policy.maximumMemory ?? ""}`,
    memScaleUpThreshold: `${policy.memScaleUpThreshold}`,
    memScaleDownThreshold: `${policy.memScaleDownThreshold}`,
    memCpuRatioRThreshold: `${policy.memCpuRatioRThreshold}`,
    memCpuRatioCThreshold: `${policy.memCpuRatioCThreshold}`,
    percentile: `${policy.percentile}`,
    useHorizontalScale: policy.useHorizontalScale,
    metricLookbackSeconds: `${policy.metricLookbackSeconds}`,
    postScaleUpCooldownSeconds: `${policy.postScaleUpCooldownSeconds}`,
    postScaleDownCooldownSeconds: `${policy.postScaleDownCooldownSeconds}`,
    postReleaseCooldownSeconds: `${policy.postReleaseCooldownSeconds}`,
  };
};

const num = <T extends number | null>(value: string, fallback: T) => {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

// Fallbacks come from the policy being edited, not literals: the inputs aren't
// `required`, so a cleared field must not silently rewrite the saved value.
const applyFormValues = (
  policy: DeployServiceSizingPolicy,
  values: PolicyFormValues,
): DeployServiceSizingPolicy => ({
  ...policy,
  label: values.label,
  minContainers: num(values.minContainers, policy.minContainers),
  maxContainers: num(values.maxContainers, policy.maxContainers),
  scaleUpStep: num(values.scaleUpStep, policy.scaleUpStep),
  scaleDownStep: num(values.scaleDownStep, policy.scaleDownStep),
  maxCpuThreshold: num(values.maxCpuThreshold, policy.maxCpuThreshold),
  minCpuThreshold: num(values.minCpuThreshold, policy.minCpuThreshold),
  minimumMemory: num(values.minimumMemory, policy.minimumMemory),
  maximumMemory: num(values.maximumMemory, policy.maximumMemory),
  memScaleUpThreshold: num(
    values.memScaleUpThreshold,
    policy.memScaleUpThreshold,
  ),
  memScaleDownThreshold: num(
    values.memScaleDownThreshold,
    policy.memScaleDownThreshold,
  ),
  memCpuRatioRThreshold: num(
    values.memCpuRatioRThreshold,
    policy.memCpuRatioRThreshold,
  ),
  memCpuRatioCThreshold: num(
    values.memCpuRatioCThreshold,
    policy.memCpuRatioCThreshold,
  ),
  percentile: num(values.percentile, policy.percentile),
  useHorizontalScale: values.useHorizontalScale,
  metricLookbackSeconds: num(
    values.metricLookbackSeconds,
    policy.metricLookbackSeconds,
  ),
  postScaleUpCooldownSeconds: num(
    values.postScaleUpCooldownSeconds,
    policy.postScaleUpCooldownSeconds,
  ),
  postScaleDownCooldownSeconds: num(
    values.postScaleDownCooldownSeconds,
    policy.postScaleDownCooldownSeconds,
  ),
  postReleaseCooldownSeconds: num(
    values.postReleaseCooldownSeconds,
    policy.postReleaseCooldownSeconds,
  ),
});

// Daily: one schedule per active day, the same window repeating on each — so a
// span longer than one night isn't expressible there by design.
// Range: a single window that runs once a week and may cover several days.
const toSchedules = (
  policyId: string,
  values: PolicyFormValues,
): DeployServiceSizingPolicyScheduleV2[] => {
  if (values.scheduleType === "range") {
    return [
      defaultServiceSizingPolicyScheduleV2({
        id: `${policyId}-range`,
        serviceSizingPolicyId: policyId,
        type: "range",
        startDay: values.startDay,
        startTime: values.startTime,
        endDay: values.endDay,
        endTime: values.endTime,
      }),
    ];
  }

  return values.activeDays.map((startDay) =>
    defaultServiceSizingPolicyScheduleV2({
      id: `${policyId}-${startDay}`,
      serviceSizingPolicyId: policyId,
      type: "daily",
      startDay,
      startTime: values.startTime,
      endDay: wrapsOvernight(values.startTime, values.endTime)
        ? (startDay % 7) + 1
        : startDay,
      endTime: values.endTime,
    }),
  );
};

// An empty time input would serialize to NaN minutes and smear the policy
// across every day of the chart. A range always has both days from a select,
// so only Daily can be missing one.
const scheduleIsComplete = (values: PolicyFormValues) => {
  if (!values.startTime || !values.endTime) return false;
  if (values.scheduleType === "range") return true;
  return values.activeDays.length > 0;
};

// The shared `Label` is 16px semibold (tokens.type.h4); Figma's field labels
// are 12px medium. A className override can't win — Tailwind emits `text-md`
// after `text-sm` — so this form labels its fields itself.
const FieldLabel = ({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string;
  required?: boolean;
  children: React.ReactNode;
}) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium text-black">
    {children}
    {required ? <span className="text-red-400">*</span> : null}
  </label>
);

// Label above, control, optional hint below (FormGroup puts hints above).
const Field = ({
  label,
  htmlFor,
  required,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1 min-w-0">
    <FieldLabel htmlFor={htmlFor} required={required}>
      {label}
    </FieldLabel>
    {children}
    {hint ? <div className="text-black-500 text-sm">{hint}</div> : null}
  </div>
);

const TextField = ({
  label,
  id,
  value,
  onChange,
  hint,
  inputMode = "numeric",
}: {
  label: string;
  id: string;
  value: string;
  onChange: (next: string) => void;
  hint?: string;
  inputMode?: "numeric" | "decimal" | "text";
}) => (
  <Field label={label} htmlFor={id} required hint={hint}>
    <Input
      id={id}
      name={id}
      type="text"
      inputMode={inputMode}
      className="w-full"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  </Field>
);

const DayToggles = ({
  activeDays,
  onToggle,
}: {
  activeDays: number[];
  onToggle: (iso: number) => void;
}) => (
  <div className="flex gap-1.5" role="group" aria-label="Active Days">
    {WEEKDAYS.map((day) => {
      const on = activeDays.includes(day.iso);
      return (
        <button
          key={day.iso}
          type="button"
          aria-pressed={on}
          aria-label={day.label}
          onClick={() => onToggle(day.iso)}
          className={`h-7 w-7 rounded-md text-sm font-medium ${
            on
              ? "bg-indigo-500 text-white"
              : "bg-black-100 text-black-400 hover:bg-black-200"
          }`}
        >
          {day.label[0]}
        </button>
      );
    })}
  </div>
);

const SCHEDULE_TYPES: {
  value: ServiceSizingPolicyScheduleTypeV2;
  label: string;
  hint: string;
}[] = [
  {
    value: "daily",
    label: "Daily",
    hint: "The same window, repeated on every day you pick",
  },
  {
    value: "range",
    label: "Range",
    hint: "One continuous window that can span several days",
  },
];

// Segmented Daily/Range switch. Follows DayToggles' aria-pressed buttons
// rather than radios — it reads as one control with two states.
const ScheduleTypeTabs = ({
  value,
  onChange,
}: {
  value: ServiceSizingPolicyScheduleTypeV2;
  onChange: (next: ServiceSizingPolicyScheduleTypeV2) => void;
}) => (
  <div
    className="flex items-center gap-1 rounded-lg border border-black-100 bg-black-50 p-[3px]"
    role="group"
    aria-label="Schedule type"
  >
    {SCHEDULE_TYPES.map((scheduleType) => {
      const on = scheduleType.value === value;
      return (
        <button
          key={scheduleType.value}
          type="button"
          aria-pressed={on}
          onClick={() => onChange(scheduleType.value)}
          className={`flex-1 h-7 rounded-md text-sm font-medium ${
            on
              ? "bg-white border border-black-200 text-black"
              : "text-black-600 hover:bg-white"
          }`}
        >
          {scheduleType.label}
        </button>
      );
    })}
  </div>
);

const DAY_OPTIONS = WEEKDAYS.map((day) => ({
  label: day.label,
  value: `${day.iso}`,
}));

const DayField = ({
  label,
  id,
  value,
  onChange,
}: {
  label: string;
  id: string;
  value: number;
  onChange: (next: number) => void;
}) => (
  <Field label={label} htmlFor={id} required>
    <Select
      id={id}
      ariaLabel={label}
      className="w-full"
      value={`${value}`}
      options={DAY_OPTIONS}
      onSelect={(opt) => onChange(Number(opt.value))}
    />
  </Field>
);

const TimeField = ({
  label,
  id,
  value,
  onChange,
  badge,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (next: string) => void;
  badge?: React.ReactNode;
}) => (
  <Field label={label} htmlFor={id} required>
    <div className="flex items-center gap-1.5">
      <Input
        id={id}
        name={id}
        type="time"
        required
        className="w-full"
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
      {badge}
    </div>
  </Field>
);

// Range: one continuous window, so both ends carry a day as well as a time.
const RangeScheduleFields = ({
  values,
  update,
}: {
  values: PolicyFormValues;
  update: (patch: Partial<PolicyFormValues>) => void;
}) => (
  <>
    <div className="grid grid-cols-2 gap-3">
      <DayField
        label="Start day"
        id="start-day"
        value={values.startDay}
        onChange={(startDay) => update({ startDay })}
      />
      <TimeField
        label="Start time (UTC)"
        id="start-time"
        value={values.startTime}
        onChange={(startTime) => update({ startTime })}
      />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <DayField
        label="End day"
        id="end-day"
        value={values.endDay}
        onChange={(endDay) => update({ endDay })}
      />
      <TimeField
        label="End time (UTC)"
        id="end-time"
        value={values.endTime}
        onChange={(endTime) => update({ endTime })}
      />
    </div>
  </>
);

// The +1 badge on End time, when a Daily window runs past midnight.
const OvernightBadge = () => (
  <span className="text-orange-400 bg-orange-100 text-sm font-semibold rounded px-1.5 py-0.5">
    +1
  </span>
);

// Daily: one time window repeated on each picked day, so days are toggles.
const DailyScheduleFields = ({
  values,
  update,
  overnight,
}: {
  values: PolicyFormValues;
  update: (patch: Partial<PolicyFormValues>) => void;
  overnight: boolean;
}) => {
  const toggleDay = (iso: number) =>
    update({
      activeDays: values.activeDays.includes(iso)
        ? values.activeDays.filter((d) => d !== iso)
        : [...values.activeDays, iso].sort((a, b) => a - b),
    });

  return (
    <>
      <div className="flex flex-col gap-1">
        <FieldLabel required>Active Days</FieldLabel>
        <DayToggles activeDays={values.activeDays} onToggle={toggleDay} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <TimeField
          label="Start time (UTC)"
          id="start-time"
          value={values.startTime}
          onChange={(startTime) => update({ startTime })}
        />
        <TimeField
          label="End time (UTC)"
          id="end-time"
          value={values.endTime}
          onChange={(endTime) => update({ endTime })}
          badge={overnight ? <OvernightBadge /> : null}
        />
      </div>
    </>
  );
};

const ScheduleFields = ({
  values,
  update,
  hasConflict,
}: {
  values: PolicyFormValues;
  update: (patch: Partial<PolicyFormValues>) => void;
  hasConflict: boolean;
}) => {
  const isRange = values.scheduleType === "range";
  // A range spanning days is the normal case, so only Daily flags the wrap.
  const overnight =
    !isRange && wrapsOvernight(values.startTime, values.endTime);
  const scheduleType = SCHEDULE_TYPES.find(
    (t) => t.value === values.scheduleType,
  );

  return (
    <div className="flex flex-col gap-2.5 border border-black-100 bg-black-50 rounded-md p-2.5">
      <div className="text-xs font-semibold text-black-500 tracking-wide uppercase">
        Schedule
      </div>

      <ScheduleTypeTabs
        value={values.scheduleType}
        onChange={(scheduleType) => update({ scheduleType })}
      />
      {scheduleType ? (
        <div className="text-black-500 text-sm">{scheduleType.hint}</div>
      ) : null}

      {isRange ? (
        <RangeScheduleFields values={values} update={update} />
      ) : (
        <DailyScheduleFields
          values={values}
          update={update}
          overnight={overnight}
        />
      )}

      {overnight ? (
        <Banner variant="warning">
          <span className="font-semibold">Overnight policy</span> — active from{" "}
          {values.startTime} until {values.endTime} the following morning. The
          continuation shows on the next-day row in the timeline.
        </Banner>
      ) : null}

      {hasConflict ? (
        <Banner variant="error">
          <span className="font-semibold">Conflict Detected</span> — This policy
          is overlapping with at least one other policy.
        </Banner>
      ) : null}

      {!isRange && values.activeDays.length === 0 ? (
        <Banner variant="info">
          Pick at least one day or this policy never runs.
        </Banner>
      ) : null}
    </div>
  );
};

const VerticalFields = ({
  values,
  update,
}: {
  values: PolicyFormValues;
  update: (patch: Partial<PolicyFormValues>) => void;
}) => (
  <>
    <div className="grid grid-cols-2 gap-3">
      <TextField
        label="Min Memory (MB)"
        id="minimum-memory"
        value={values.minimumMemory}
        onChange={(minimumMemory) => update({ minimumMemory })}
        hint="Lowest memory this service can scale to"
      />
      <TextField
        label="Max Memory (MB)"
        id="maximum-memory"
        value={values.maximumMemory}
        onChange={(maximumMemory) => update({ maximumMemory })}
        hint="Highest memory this service can scale to"
      />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <TextField
        label="Memory Scale Up %"
        id="memory-scale-up"
        inputMode="decimal"
        value={values.memScaleUpThreshold}
        onChange={(memScaleUpThreshold) => update({ memScaleUpThreshold })}
        hint="Percent of the current limit that triggers a scale up"
      />
      <TextField
        label="Memory Scale Down %"
        id="memory-scale-down"
        inputMode="decimal"
        value={values.memScaleDownThreshold}
        onChange={(memScaleDownThreshold) => update({ memScaleDownThreshold })}
        hint="Percent of the next smallest limit that triggers a scale down"
      />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <TextField
        label="Memory-Optimized Ratio"
        id="r-ratio"
        inputMode="decimal"
        value={values.memCpuRatioRThreshold}
        onChange={(memCpuRatioRThreshold) => update({ memCpuRatioRThreshold })}
        hint="Memory (GB) to CPU ratio above which the service moves to R"
      />
      <TextField
        label="Compute-Optimized Ratio"
        id="c-ratio"
        inputMode="decimal"
        value={values.memCpuRatioCThreshold}
        onChange={(memCpuRatioCThreshold) => update({ memCpuRatioCThreshold })}
        hint="Memory (GB) to CPU ratio below which the service moves to C"
      />
    </div>
  </>
);

const AdvancedFields = ({
  values,
  update,
  isVertical,
}: {
  values: PolicyFormValues;
  update: (patch: Partial<PolicyFormValues>) => void;
  isVertical: boolean;
}) => (
  <div className="flex flex-col gap-3 pb-2">
    <TextField
      label="Percentile"
      id="percentile"
      value={values.percentile}
      onChange={(percentile) => update({ percentile })}
      hint={
        isVertical
          ? "Percentile to use for RAM and CPU"
          : "Percentile to use for CPU"
      }
    />
    {/* Restart-Free Scale is horizontal-only. */}
    {isVertical ? null : (
      <Field
        label="Restart-Free Scale"
        htmlFor="restart-free-scale"
        required
        hint="Scale containers without restarting all containers"
      >
        <Select
          id="restart-free-scale"
          ariaLabel="restart-free-scale"
          value={values.useHorizontalScale ? "enabled" : "disabled"}
          onSelect={(opt) =>
            update({ useHorizontalScale: opt.value === "enabled" })
          }
          options={[
            { label: "Enabled", value: "enabled" },
            { label: "Disabled", value: "disabled" },
          ]}
        />
      </Field>
    )}
    <TextField
      label="Metrics Lookback (s)"
      id="metrics-lookback"
      value={values.metricLookbackSeconds}
      onChange={(metricLookbackSeconds) => update({ metricLookbackSeconds })}
      hint="Time interval in seconds to fetch metrics for evaluation"
    />
    <TextField
      label="Scale Up Cooldown (s)"
      id="scale-up-cooldown"
      value={values.postScaleUpCooldownSeconds}
      onChange={(postScaleUpCooldownSeconds) =>
        update({ postScaleUpCooldownSeconds })
      }
      hint="Seconds to wait after an automated scale to potentially scale up"
    />
    <TextField
      label="Scale Down Cooldown (s)"
      id="scale-down-cooldown"
      value={values.postScaleDownCooldownSeconds}
      onChange={(postScaleDownCooldownSeconds) =>
        update({ postScaleDownCooldownSeconds })
      }
      hint="Seconds to wait after an automated scale to potentially scale down"
    />
    <TextField
      label="Post-Release Cooldown (s)"
      id="post-release-cooldown"
      value={values.postReleaseCooldownSeconds}
      onChange={(postReleaseCooldownSeconds) =>
        update({ postReleaseCooldownSeconds })
      }
      hint="Seconds to ignore metrics after a scale event, for stabilization"
    />
    <Button
      variant="white"
      size="sm"
      className="w-fit"
      onClick={() =>
        update({
          percentile: `${ADVANCED_DEFAULTS.percentile}`,
          useHorizontalScale: ADVANCED_DEFAULTS.useHorizontalScale,
          metricLookbackSeconds: `${ADVANCED_DEFAULTS.metricLookbackSeconds}`,
          postScaleUpCooldownSeconds: `${ADVANCED_DEFAULTS.postScaleUpCooldownSeconds}`,
          postScaleDownCooldownSeconds: `${ADVANCED_DEFAULTS.postScaleDownCooldownSeconds}`,
          postReleaseCooldownSeconds: `${ADVANCED_DEFAULTS.postReleaseCooldownSeconds}`,
        })
      }
    >
      <IconRefresh variant="sm" className="mr-1" />
      Reset to Defaults
    </Button>
  </div>
);

// Lets the panel header's Save button submit the form it sits outside of.
const POLICY_FORM_ID = "scheduled-policy-form";

/**
 * Edits one policy's window and scaling thresholds. Mount with
 * `key={policy.id}` so switching policies re-seeds the inputs.
 *
 * Save lives in the panel header, outside this form, and reaches it through
 * `form={POLICY_FORM_ID}`.
 */
const ScheduledPolicyForm = ({
  policy,
  policySchedules,
  isBaseline,
  isVertical,
  hasConflict,
  onSave,
  onDelete,
  onDraftChange,
  onDirtyChange,
}: {
  policy: DeployServiceSizingPolicy;
  policySchedules: DeployServiceSizingPolicyScheduleV2[];
  isBaseline: boolean;
  isVertical: boolean;
  hasConflict: boolean;
  onSave: (
    policy: DeployServiceSizingPolicy,
    schedules: DeployServiceSizingPolicyScheduleV2[],
  ) => void;
  onDelete: () => void;
  onDraftChange: (schedules: DeployServiceSizingPolicyScheduleV2[]) => void;
  onDirtyChange: (dirty: boolean) => void;
}) => {
  // Snapshot of the seeded values, to tell the header whether anything changed.
  const [initialValues] = useState(() => toFormValues(policy, policySchedules));
  const [values, setValues] = useState(initialValues);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Baseline covers all unclaimed time; vertical has no schedule at all.
  const hasWindow = !isBaseline && !isVertical;
  const update = (patch: Partial<PolicyFormValues>) => {
    const next = { ...values, ...patch };
    setValues(next);
    // Key order is fixed by the spread above, and `activeDays` is kept sorted.
    onDirtyChange(JSON.stringify(next) !== JSON.stringify(initialValues));
    // No window to draw, and a half-typed one serializes to NaN minutes,
    // which would smear the policy across the whole chart.
    if (!hasWindow) return;
    onDraftChange(scheduleIsComplete(next) ? toSchedules(policy.id, next) : []);
  };
  // An overlap is rejected by the backend, so block the save outright.
  const canSave = (!hasWindow || scheduleIsComplete(values)) && !hasConflict;

  return (
    <form
      id={POLICY_FORM_ID}
      className="flex flex-col gap-3.5 p-3.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave) return;
        onSave(
          applyFormValues(policy, values),
          hasWindow ? toSchedules(policy.id, values) : [],
        );
      }}
    >
      <Field label="Label" htmlFor="policy-label" required>
        <Input
          id="policy-label"
          name="policy-label"
          type="text"
          className="w-full"
          value={values.label}
          onChange={(e) => update({ label: e.currentTarget.value })}
        />
      </Field>

      {isVertical ? null : isBaseline ? (
        <Banner variant="info">
          The baseline policy covers all time no other policy claims, so it has
          no schedule.
        </Banner>
      ) : (
        <ScheduleFields
          values={values}
          update={update}
          hasConflict={hasConflict}
        />
      )}

      {isVertical ? (
        <VerticalFields values={values} update={update} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Min Containers"
              id="min-containers"
              value={values.minContainers}
              onChange={(minContainers) => update({ minContainers })}
            />
            <TextField
              label="Max Containers"
              id="max-containers"
              value={values.maxContainers}
              onChange={(maxContainers) => update({ maxContainers })}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Scale Up Steps"
              id="scale-up-step"
              value={values.scaleUpStep}
              onChange={(scaleUpStep) => update({ scaleUpStep })}
              hint="Containers added per event"
            />
            <TextField
              label="Scale Down Steps"
              id="scale-down-step"
              value={values.scaleDownStep}
              onChange={(scaleDownStep) => update({ scaleDownStep })}
              hint="Containers removed per event"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Scale Up CPU"
              id="scale-up-cpu"
              inputMode="decimal"
              value={values.maxCpuThreshold}
              onChange={(maxCpuThreshold) => update({ maxCpuThreshold })}
              hint="Percent of CPU usage that will trigger scaling"
            />
            <TextField
              label="Scale Down CPU"
              id="scale-down-cpu"
              inputMode="decimal"
              value={values.minCpuThreshold}
              onChange={(minCpuThreshold) => update({ minCpuThreshold })}
              hint="Percent of CPU usage that will trigger scaling"
            />
          </div>
        </>
      )}

      <hr className="border-black-100" />

      <Disclosure
        labelWhenClosed="Advanced Settings"
        labelWhenOpen="Advanced Settings"
        open={advancedOpen}
        onToggle={() => setAdvancedOpen(!advancedOpen)}
      >
        <AdvancedFields
          values={values}
          update={update}
          isVertical={isVertical}
        />

        <hr className="border-black-100" />

        <Button
          variant="white"
          size="sm"
          className="w-fit"
          disabled={isBaseline || isVertical}
          aria-label="Delete policy"
          onClick={onDelete}
        >
          <IconTrash variant="sm" />
        </Button>
      </Disclosure>
    </form>
  );
};

// Hour ruler ticks across the 24h width.
const HOUR_MARKS = [0, 6, 12, 18] as const;

// One color per policy, cycled by position; red/black-gray reserved.
// `hoverFill` is a literal class so Tailwind's JIT scanner can see it.
const POLICY_COLORS = [
  {
    fill: "bg-indigo-150",
    border: "border-indigo-400",
    hoverFill: "bg-indigo-400",
  },
  { fill: "bg-cyan-200", border: "border-cyan-400", hoverFill: "bg-cyan-400" },
  { fill: "bg-lime-100", border: "border-lime-400", hoverFill: "bg-lime-400" },
  {
    fill: "bg-orange-100",
    border: "border-orange-400",
    hoverFill: "bg-orange-400",
  },
  {
    fill: "bg-lavender-200",
    border: "border-lavender-400",
    hoverFill: "bg-lavender-400",
  },
  {
    fill: "bg-maroon-100",
    border: "border-maroon-400",
    hoverFill: "bg-maroon-400",
  },
] as const;

// Red diagonal stripes (red-400 over red-100) for double-claimed time.
const CONFLICT_FILL =
  "bg-[repeating-linear-gradient(45deg,#FDE3E3_0px,#FDE3E3_5px,#AD1A1A_5px,#AD1A1A_10px)]";
const CONFLICT_BORDER = "border-red-500";
// Hover key for conflicts; matches no legend entry, so nothing else lights up.
const CONFLICT_KEY = "__conflict__";

// Hover: border goes full black; fill takes a literal ~400-shade class.
const getSegmentColors = (colorIndex: number | null, highlighted: boolean) => {
  if (colorIndex === null) {
    return {
      fill: highlighted ? "bg-black-300" : "bg-black-100",
      border: highlighted ? "border-black" : "border-black-300",
    };
  }
  const color = POLICY_COLORS[colorIndex];
  return {
    fill: highlighted ? color.hoverFill : color.fill,
    border: highlighted ? "border-black" : color.border,
  };
};

// Swatch + label; hovering highlights matching bars too (see getSegmentColors).
function LegendItem({
  fill,
  border,
  label,
  onMouseEnter,
  onMouseLeave,
}: {
  fill: string;
  border: string;
  label: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  return (
    <div
      className="flex items-center gap-1.5 text-sm text-black-500 cursor-pointer"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <span className={`h-3.5 w-3.5 rounded-sm border ${fill} ${border}`} />
      {label}
    </div>
  );
}

// Hover tooltip content for a policy (or the baseline policy): label,
// optional subtitle (e.g. a schedule's days/time), and scaling thresholds.
function SegmentTooltip({
  policy,
  subtitle,
  className,
  referenceClassName,
  children,
}: {
  policy: DeployServiceSizingPolicy;
  subtitle?: string;
  className?: string;
  referenceClassName?: string;
  children: React.ReactNode;
}) {
  const rows: [string, React.ReactNode][] = [
    ["Min containers:", policy.minContainers ?? "–"],
    ["Max containers:", policy.maxContainers ?? "–"],
    ["Scale up CPU:", policy.maxCpuThreshold ?? "–"],
    ["Scale down CPU:", policy.minCpuThreshold ?? "–"],
  ];

  return (
    <Tooltip
      compact
      className={className}
      referenceClassName={referenceClassName}
      text={
        <div className="flex flex-col gap-1 px-1 py-1">
          <div>
            <div className="font-semibold">{policy.label || policy.id}</div>
            {subtitle ? (
              <div className="flex flex-col gap-0.5">{subtitle}</div>
            ) : null}
          </div>
          <div className="flex flex-col gap-0.5 mt-1.5">
            {rows.map(([label, value]) => (
              <div
                key={label}
                className="flex items-baseline justify-between gap-4"
              >
                <span className="whitespace-nowrap">{label}</span>
                <span className="text-right">{value}</span>
              </div>
            ))}
          </div>
        </div>
      }
    >
      {children}
    </Tooltip>
  );
}

// A legend entry: the swatch/label, wrapped in its policy's tooltip.
function LegendEntry({
  policy,
  subtitle,
  fill,
  border,
  label,
  onMouseEnter,
  onMouseLeave,
}: {
  policy: DeployServiceSizingPolicy;
  subtitle?: string;
  fill: string;
  border: string;
  label: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  return (
    <SegmentTooltip policy={policy} subtitle={subtitle}>
      <LegendItem
        fill={fill}
        border={border}
        label={label}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    </SegmentTooltip>
  );
}

// One weekday's bar: baseline + policy segments, each hoverable.
function WeekdayRow({
  segments,
  baselinePolicy,
  hoveredKey,
  onHover,
}: {
  segments: PolicySegment[];
  baselinePolicy: DeployServiceSizingPolicy;
  hoveredKey: string | null;
  onHover: (key: string | null) => void;
}) {
  return (
    <div className="relative h-10 w-full">
      {fillDayGaps(resolveOverlaps(segments)).map((seg: DaySegment, i) => {
        const conflict = seg.conflictPolicies;
        const key = conflict ? CONFLICT_KEY : (seg.policy?.id ?? BASELINE_KEY);
        const colors = conflict
          ? { fill: CONFLICT_FILL, border: CONFLICT_BORDER }
          : getSegmentColors(seg.colorIndex, hoveredKey === key);
        const colorBox = (
          <div
            className={`h-full w-full rounded-[2px] border ${colors.fill} ${colors.border}`}
            title={
              conflict
                ? `Schedule conflict: ${conflict
                    .map((p) => p.label || p.id)
                    .join(" + ")}`
                : undefined
            }
            onMouseEnter={() => onHover(key)}
            onMouseLeave={() => onHover(null)}
          />
        );
        const tooltipPolicy = seg.policy ?? baselinePolicy;
        const subtitle = seg.schedule
          ? summarizeSchedule(seg.schedule)
          : "All unscheduled time";

        // Conflicts carry a native title instead of a policy tooltip — there
        // is no single policy to describe.
        let content = colorBox;
        if (!conflict) {
          content = (
            <SegmentTooltip
              policy={tooltipPolicy}
              subtitle={subtitle}
              className="h-full w-full"
              referenceClassName="block h-full w-full"
            >
              {colorBox}
            </SegmentTooltip>
          );
        }

        return (
          <div
            key={i}
            className="absolute inset-y-0 cursor-pointer"
            style={{
              left: `calc(${seg.startPct}% + 1px)`,
              width: `calc(${seg.endPct - seg.startPct}% - 2px)`,
            }}
          >
            {content}
          </div>
        );
      })}
    </div>
  );
}

// Hour ruler, weekday bars, a red current-time line, and a legend. Hover is
// shared with ScheduledPolicySidebar so matching rows and bars light up.
function WeeklyScheduleChart({
  now = new Date(),
  policies,
  schedules,
  baselinePolicy,
  hoveredKey,
  onHoverChange,
}: {
  now?: Date;
  policies: DeployServiceSizingPolicy[];
  schedules: DeployServiceSizingPolicyScheduleV2[];
  baselinePolicy: DeployServiceSizingPolicy;
  hoveredKey: string | null;
  onHoverChange: (key: string | null) => void;
}) {
  const nowPct = (minutesIntoDayUtc(now) / (24 * 60)) * 100;
  const today = isoWeekdayUtc(now);
  const segmentsByDay = buildSegmentsByDay(
    policies,
    schedules,
    POLICY_COLORS.length,
  );

  return (
    <div className="flex flex-col gap-2.5">
      <div
        className="flex gap-2.5"
        role="img"
        aria-label="Weekly schedule (UTC)"
      >
        <div className="flex flex-col">
          {/* Spacer to align with the hour ruler row */}
          <div className="h-5" />
          <div className="flex flex-col gap-1.5 mt-1.5">
            {WEEKDAYS.map((day) => (
              <div
                key={day.iso}
                className={`h-10 flex items-center text-sm ${
                  day.iso === today ? "font-bold text-black" : "text-black-400"
                }`}
              >
                {day.label}
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1">
          {/* Hour ruler */}
          <div className="relative h-5 text-xs text-black-400">
            {HOUR_MARKS.map((hour) => (
              <span
                key={hour}
                className="absolute"
                style={{
                  left: `${(hour / 24) * 100}%`,
                  transform: hour === 0 ? undefined : "translateX(-50%)",
                }}
              >
                {hour}:00
              </span>
            ))}
          </div>

          <div className="relative mt-1.5">
            <div className="flex flex-col gap-1.5">
              {WEEKDAYS.map((day) => (
                <WeekdayRow
                  key={day.iso}
                  segments={segmentsByDay.get(day.iso) ?? []}
                  baselinePolicy={baselinePolicy}
                  hoveredKey={hoveredKey}
                  onHover={onHoverChange}
                />
              ))}
            </div>

            {/* Current time, UTC — line plus a thermometer-style dot at the top */}
            <div
              className="absolute -top-2.5 bottom-0 w-0.5 bg-red-400"
              style={{ left: `${nowPct}%`, transform: "translateX(-50%)" }}
            />
            <div
              className="absolute -top-2.5 h-2.5 w-2.5 rounded-full bg-red-400"
              style={{ left: `${nowPct}%`, transform: "translateX(-50%)" }}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <LegendEntry
          policy={baselinePolicy}
          subtitle="Default — all uncovered time"
          {...getSegmentColors(null, hoveredKey === BASELINE_KEY)}
          label="Baseline"
          onMouseEnter={() => onHoverChange(BASELINE_KEY)}
          onMouseLeave={() => onHoverChange(null)}
        />
        {policies.map((policy, i) => {
          const colorIndex = i % POLICY_COLORS.length;
          return (
            <LegendEntry
              key={policy.id}
              policy={policy}
              {...getSegmentColors(colorIndex, hoveredKey === policy.id)}
              label={policy.label || `Policy ${policy.id}`}
              onMouseEnter={() => onHoverChange(policy.id)}
              onMouseLeave={() => onHoverChange(null)}
            />
          );
        })}
      </div>
    </div>
  );
}

function SidebarRow({
  dot,
  label,
  subtitle,
  badge,
  highlighted,
  conflict,
  accentFillClass,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  dot: React.ReactNode;
  label: string;
  subtitle: React.ReactNode;
  badge?: React.ReactNode;
  highlighted: boolean;
  conflict?: boolean;
  accentFillClass: string;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  // A conflict outranks the selected/hovered white, so the row stays flagged
  // while it is being edited.
  let background = "";
  if (conflict) background = "bg-red-100";
  else if (highlighted) background = "bg-white";

  return (
    <div
      className={`relative flex items-center gap-2 px-2.5 py-2 cursor-pointer ${background}`}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* 4px accent bar on the right edge of the selected/hovered row */}
      {highlighted ? (
        <span className={`absolute inset-y-0 right-0 w-1 ${accentFillClass}`} />
      ) : null}
      {dot}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <div
            className={`text-sm flex-1 min-w-0 truncate ${highlighted ? "font-semibold text-black" : "text-black-600"}`}
          >
            {label}
          </div>
          {conflict ? (
            <IconAlertTriangle
              variant="sm"
              color="#AD1A1A"
              title={`${label} overlaps another policy`}
            />
          ) : null}
        </div>
        <div
          className={`text-[10px] flex items-center gap-1.5 ${
            highlighted ? "text-black-500" : "truncate text-black-400"
          }`}
        >
          {subtitle}
          {badge}
        </div>
      </div>
    </div>
  );
}

// Policy list sidebar: baseline + each policy, with a dot, label, and a
// days/time summary.
function ScheduledPolicySidebar({
  policies,
  schedules,
  baselinePolicy,
  maxPolicies,
  conflictPolicyIds,
  hoveredKey,
  onHoverChange,
  selectedKey,
  onSelectChange,
  onAddPolicy,
}: {
  policies: DeployServiceSizingPolicy[];
  schedules: DeployServiceSizingPolicyScheduleV2[];
  baselinePolicy: DeployServiceSizingPolicy;
  maxPolicies: number;
  /** Ids of policies whose windows overlap another policy's. */
  conflictPolicyIds: Set<string>;
  hoveredKey: string | null;
  onHoverChange: (key: string | null) => void;
  selectedKey: string | null;
  onSelectChange: (key: string | null) => void;
  onAddPolicy: () => void;
}) {
  const schedulesByPolicyId = new Map<
    string,
    DeployServiceSizingPolicyScheduleV2[]
  >();
  for (const schedule of schedules) {
    const existing = schedulesByPolicyId.get(schedule.serviceSizingPolicyId);
    if (existing) {
      existing.push(schedule);
    } else {
      schedulesByPolicyId.set(schedule.serviceSizingPolicyId, [schedule]);
    }
  }

  const policyCount = policies.length + 1; // + baseline
  const atMaxPolicies = policyCount >= maxPolicies;

  return (
    // Fills the panel's height so the gray runs to the bottom; the parent
    // row stretches its columns.
    <div className="w-[168px] shrink-0 flex flex-col border-r border-black-100 bg-black-50">
      <div className="px-3 pt-3 pb-2 text-xs font-semibold text-black-400 tracking-wide uppercase border-b border-black-100">
        Policies
      </div>
      <div className="flex flex-col divide-y divide-black-100">
        <SidebarRow
          dot={
            <span className="h-2.5 w-2.5 rounded-full border-2 border-black-300 bg-white shrink-0" />
          }
          label={baselinePolicy.label || "Baseline"}
          subtitle="Always active"
          badge={
            <span className="text-green-400 bg-green-100 font-semibold rounded-full px-1.5 py-0.5 whitespace-nowrap">
              Baseline
            </span>
          }
          highlighted={
            hoveredKey === BASELINE_KEY || selectedKey === BASELINE_KEY
          }
          accentFillClass="bg-black"
          onClick={() => onSelectChange(BASELINE_KEY)}
          onMouseEnter={() => onHoverChange(BASELINE_KEY)}
          onMouseLeave={() => onHoverChange(null)}
        />
        {policies.map((policy, i) => {
          const color = POLICY_COLORS[i % POLICY_COLORS.length];
          return (
            <SidebarRow
              key={policy.id}
              dot={
                <span
                  className={`h-2.5 w-2.5 rounded-full shrink-0 ${color.hoverFill}`}
                />
              }
              label={policy.label || `Policy ${policy.id}`}
              subtitle={summarizePolicySchedules(
                schedulesByPolicyId.get(policy.id) ?? [],
              )}
              highlighted={
                hoveredKey === policy.id || selectedKey === policy.id
              }
              conflict={conflictPolicyIds.has(policy.id)}
              accentFillClass={color.hoverFill}
              onClick={() => onSelectChange(policy.id)}
              onMouseEnter={() => onHoverChange(policy.id)}
              onMouseLeave={() => onHoverChange(null)}
            />
          );
        })}
      </div>
      <div className="border-t border-black-100 p-2">
        <button
          type="button"
          onClick={onAddPolicy}
          disabled={atMaxPolicies}
          className="w-full flex items-center justify-center gap-1 rounded-[6px] border border-black-200 text-sm text-black-500 py-1.5 px-2 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
        >
          + Add Policy ({policyCount}/{maxPolicies})
        </button>
      </div>
    </div>
  );
}

const MAX_POLICIES = 7;

const AUTOSCALING_DOCS_URL =
  "https://www.aptible.com/docs/core-concepts/scaling/app-scaling#autoscaling";

/**
 * Confirms discarding the horizontal policy set. Shown on every switch away
 * from Horizontal, since the policies and schedules are purged either way.
 */
const SwitchModeModal = ({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <ModalOverlay aria-label="Switch autoscaling mode" onClose={onCancel}>
    <Modal isOpen>
      <ModalHeading
        icon={<IconAlertTriangle color="#AD1A1A" />}
        title="Don't lose your policy settings"
      />
      <ModalBody className="flex flex-col gap-4">
        <p className="text-black">
          Your policy settings will not be saved if you disable or switch to
          Vertical Autoscaling. Are you sure you want to turn off Horizontal
          Autoscaling?
        </p>
        <p className="text-black">
          If you're looking for more information about Vertical Autoscaling, be
          sure to{" "}
          <a
            href={AUTOSCALING_DOCS_URL}
            target="_blank"
            rel="noreferrer"
            className="text-indigo hover:text-indigo-300"
          >
            check out the docs.
          </a>
        </p>
        <div className="flex items-center gap-2">
          <Button variant="white" onClick={onCancel}>
            Cancel and stay on this page
          </Button>
          <Button variant="delete" onClick={onConfirm}>
            Confirm and switch
          </Button>
        </div>
      </ModalBody>
    </Modal>
  </ModalOverlay>
);

export const AppDetailServiceScaleV2AutoscalingPage = () => {
  const [preview, setPreview] =
    useState<SchedulePreviewState>(initialPreviewState);
  // Mode the confirmation modal is holding, null when it's closed.
  const [pendingMode, setPendingMode] = useState<ScalingMode | null>(null);

  // Switching mode throws the policy set away. Vertical needs one policy to
  // edit since it has no sidebar; horizontal starts from baseline alone.
  const applyMode = (mode: ScalingMode) =>
    setPreview((prev) => {
      const verticalPolicy =
        mode === "vertical"
          ? defaultServiceSizingPolicy({
              id: `preview-policy-${crypto.randomUUID()}`,
              label: "Policy 1",
              autoscaling: "vertical",
              scalingEnabled: true,
            })
          : null;
      return {
        ...prev,
        mode,
        policies: verticalPolicy ? [verticalPolicy] : [],
        schedules: [],
        draft: null,
        hoveredKey: null,
        selectedKey: verticalPolicy?.id ?? BASELINE_KEY,
        formKey: prev.formKey + 1,
        dirty: false,
      };
    });

  // Only leaving Horizontal costs the user their policies, so that's the one
  // transition that has to be confirmed.
  const onModeSelect = (mode: ScalingMode) => {
    if (mode === preview.mode) return;
    if (preview.mode === "horizontal") {
      setPendingMode(mode);
      return;
    }
    applyMode(mode);
  };

  const onHoverChange = (hoveredKey: string | null) =>
    setPreview((prev) => ({ ...prev, hoveredKey }));
  const onSelectChange = (selectedKey: string | null) =>
    setPreview((prev) => ({ ...prev, selectedKey, dirty: false }));

  const onAddPolicy = () =>
    setPreview((prev) => {
      const id = `preview-policy-${crypto.randomUUID()}`;
      return {
        ...prev,
        policies: [
          ...prev.policies,
          defaultServiceSizingPolicy({
            id,
            label: `Policy ${prev.policies.length + 1}`,
            autoscaling: "horizontal",
            scalingEnabled: true,
          }),
        ],
        selectedKey: id,
        dirty: false,
      };
    });

  const onSave = (
    policy: DeployServiceSizingPolicy,
    policySchedules: DeployServiceSizingPolicyScheduleV2[],
  ) =>
    setPreview((prev) => ({
      ...prev,
      baselinePolicy:
        prev.baselinePolicy.id === policy.id ? policy : prev.baselinePolicy,
      policies: prev.policies.map((p) => (p.id === policy.id ? policy : p)),
      schedules: [
        ...prev.schedules.filter((s) => s.serviceSizingPolicyId !== policy.id),
        ...policySchedules,
      ],
      dirty: false,
    }));

  // Throws away the form's unsaved edits: drop the draft window and remount
  // the form so it re-seeds from the saved policy.
  const onCancel = () =>
    setPreview((prev) => ({
      ...prev,
      draft: null,
      formKey: prev.formKey + 1,
      dirty: false,
    }));

  const onDelete = (policyId: string) =>
    setPreview((prev) => {
      const policies = prev.policies.filter((p) => p.id !== policyId);
      return {
        ...prev,
        policies,
        schedules: prev.schedules.filter(
          (s) => s.serviceSizingPolicyId !== policyId,
        ),
        selectedKey: policies[0]?.id ?? BASELINE_KEY,
        dirty: false,
      };
    });

  const isBaseline = preview.selectedKey === BASELINE_KEY;
  const isVertical = preview.mode === "vertical";
  // Sidebar, chart and schedules are horizontal-only.
  const showSchedule = preview.mode === "horizontal";
  const selectedPolicy = isBaseline
    ? preview.baselinePolicy
    : preview.policies.find((p) => p.id === preview.selectedKey);

  // Draw the form's unsaved window in place of the selected policy's saved
  // schedules. A draft left over from another policy is ignored.
  const { draft } = preview;
  const shownSchedules =
    draft && draft.policyId === preview.selectedKey
      ? [
          ...preview.schedules.filter(
            (s) => s.serviceSizingPolicyId !== draft.policyId,
          ),
          ...draft.schedules,
        ]
      : preview.schedules;

  // Derived from the previewed windows, so an in-progress edit flags itself.
  const conflictPolicyIds = findConflictingPolicyIdsV2(shownSchedules);

  // Mirrors the form's own guard, from the previewed windows rather than its
  // inputs: an incomplete schedule contributes none, and an overlap is
  // rejected by the backend. Baseline and vertical have no window to miss.
  const canSave =
    preview.dirty &&
    !!selectedPolicy &&
    !conflictPolicyIds.has(selectedPolicy.id) &&
    (isBaseline ||
      !showSchedule ||
      shownSchedules.some(
        (s) => s.serviceSizingPolicyId === selectedPolicy.id,
      ));

  // A policy with no saved window yet is being turned on, not edited.
  const saveLabel =
    selectedPolicy &&
    preview.schedules.some((s) => s.serviceSizingPolicyId === selectedPolicy.id)
      ? "Update"
      : "Save & Activate";

  return (
    <div className="bg-white border border-black-100 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-4 bg-black-50 border-b border-black-100 px-4 py-2">
        <div className="flex items-center gap-2">
          <h1 className="text-base font-semibold text-black">Autoscaling:</h1>
          <Select<ScalingMode>
            ariaLabel="Autoscaling"
            value={preview.mode}
            onSelect={(opt) => onModeSelect(opt.value)}
            options={MODE_OPTIONS}
          />
        </div>
        {preview.mode === "off" ? null : (
          <div className="flex items-center gap-4">
            {preview.dirty ? (
              <Button variant="white" size="sm" onClick={onCancel}>
                Cancel Changes
              </Button>
            ) : null}
            <Button
              type="submit"
              form={POLICY_FORM_ID}
              size="sm"
              disabled={!canSave}
            >
              {saveLabel}
            </Button>
          </div>
        )}
      </div>

      {preview.mode === "off" ? null : (
        <div className="flex">
          {showSchedule ? (
            <>
              <ScheduledPolicySidebar
                policies={preview.policies}
                schedules={shownSchedules}
                baselinePolicy={preview.baselinePolicy}
                maxPolicies={MAX_POLICIES}
                conflictPolicyIds={conflictPolicyIds}
                hoveredKey={preview.hoveredKey}
                onHoverChange={onHoverChange}
                selectedKey={preview.selectedKey}
                onSelectChange={onSelectChange}
                onAddPolicy={onAddPolicy}
              />

              <div className="flex-1 min-w-0 p-4">
                <WeeklyScheduleChart
                  policies={preview.policies}
                  schedules={shownSchedules}
                  baselinePolicy={preview.baselinePolicy}
                  hoveredKey={preview.hoveredKey}
                  onHoverChange={onHoverChange}
                />
              </div>
            </>
          ) : null}

          {selectedPolicy ? (
            <div
              className={
                showSchedule
                  ? "w-[340px] shrink-0 border-l border-black-100"
                  : "w-full max-w-lg"
              }
            >
              <ScheduledPolicyForm
                key={`${selectedPolicy.id}-${preview.formKey}`}
                policy={selectedPolicy}
                policySchedules={preview.schedules.filter(
                  (s) => s.serviceSizingPolicyId === selectedPolicy.id,
                )}
                isBaseline={isBaseline}
                isVertical={isVertical}
                hasConflict={conflictPolicyIds.has(selectedPolicy.id)}
                onSave={onSave}
                onDelete={() => onDelete(selectedPolicy.id)}
                onDraftChange={(schedules) =>
                  setPreview((prev) => ({
                    ...prev,
                    draft: { policyId: selectedPolicy.id, schedules },
                  }))
                }
                onDirtyChange={(dirty) =>
                  setPreview((prev) =>
                    prev.dirty === dirty ? prev : { ...prev, dirty },
                  )
                }
              />
            </div>
          ) : null}
        </div>
      )}

      {pendingMode ? (
        <SwitchModeModal
          onCancel={() => setPendingMode(null)}
          onConfirm={() => {
            applyMode(pendingMode);
            setPendingMode(null);
          }}
        />
      ) : null}
    </div>
  );
};
